import { useEffect, useState } from 'react';
import { rankWith, ControlProps, isControl, resolveSchema } from '@jsonforms/core';
import { withJsonFormsControlProps, useJsonForms } from '@jsonforms/react';
import { Box, Typography } from '@mui/material';
import { apiClient } from '@/lib/apiClient';
import { LookupRendererComponent } from './LookupRenderer';

/**
 * Entity lookup with a grayed inherited value (`x-lookup` + `x-inherit` schema markers).
 *
 * The mirror of DimensionValuesEditor's inherited-defaults affordance for a plain FK field on a
 * hierarchy-node entity: when the node has no own value, the nearest ancestor's value renders
 * grayed with source attribution ("hektar · inherited from Skogsvård"); picking a value overrides
 * it for the subtree, clearing the override reverts to the grayed inherited display. The chain is
 * resolved server-side (the client cannot see ancestors above a loaded subtree root) via the
 * per-entity endpoint below, called with the parent node id read from the sibling form-data key
 * named by `x-inherit.from` — the same parentId display-context the dimension editor uses.
 */

/** One link of the effective-value chain (nearest-first), as the endpoints return it. */
interface EffectiveChainNode {
  valueId: string;
  valueCode: string;
  valueName: string;
  sourceNodeCode: string;
  sourceNodeName: string;
}

/**
 * Per-entity chain endpoints (mirror of DimensionSetEditor's INHERIT_SOURCE_BY_ENTITY map).
 * Each returns the chain nearest-first; element [0] is the effective value for a child of the
 * queried node.
 */
const CHAIN_ENDPOINT_BY_ENTITY: Record<string, (nodeId: string) => string> = {
  ProductGroup: (nodeId) => `/api/inventory/inventory-product-groups/${nodeId}/effective-unit-chain`,
};

export const inheritedLookupTester = rankWith(
  15, // above LookupRenderer (10) so the x-inherit marker wins the dispatch
  (uischema, schema, context) => {
    if (!isControl(uischema) || !schema) return false;
    const scope = (uischema as any).scope as string | undefined;
    const rootSchema = (context as any)?.rootSchema ?? schema;
    let fieldSchema: any = schema;
    if (scope) {
      try {
        fieldSchema = resolveSchema(rootSchema, scope, rootSchema) ?? schema;
      } catch {
        fieldSchema = schema;
      }
    }
    return !!fieldSchema?.['x-lookup'] && !!fieldSchema?.['x-inherit'];
  },
);

const InheritedLookupComponent = (props: ControlProps) => {
  const { data, schema, path, visible } = props;
  const ctx = useJsonForms();
  const formData: any = ctx.core?.data || {};

  const inheritCfg = (schema as any)?.['x-inherit'] as { from: string; entity: string } | undefined;
  const endpointFor = inheritCfg ? CHAIN_ENDPOINT_BY_ENTITY[inheritCfg.entity] : undefined;
  const parentId: string | null = inheritCfg ? (formData[inheritCfg.from] ?? null) : null;

  const [inherited, setInherited] = useState<EffectiveChainNode | null>(null);

  // The inherited value only matters while the own value is empty, and only when the node has a
  // parent (a root with no own value simply has no effective value — no caption).
  const wantInherited = !data && !!parentId && !!endpointFor;

  useEffect(() => {
    if (!wantInherited || !endpointFor || !parentId) {
      setInherited(null);
      return;
    }
    let cancelled = false;
    apiClient
      .get(endpointFor(parentId))
      .then((res) => {
        if (cancelled) return;
        const chain: EffectiveChainNode[] = res.data ?? [];
        setInherited(chain.length > 0 ? chain[0] : null);
      })
      .catch(() => {
        if (!cancelled) setInherited(null);
      });
    return () => {
      cancelled = true;
    };
  }, [wantInherited, endpointFor, parentId]);

  if (visible === false) return null;

  const fieldName = path.split('.').pop() || path;
  const showInherited = wantInherited && inherited !== null;

  return (
    <Box>
      <LookupRendererComponent {...props} />
      {showInherited && (
        <Typography
          variant="body2"
          data-testid={`inherited-${fieldName}`}
          sx={{
            display: 'block',
            mt: 0.5,
            color: 'text.disabled',
            fontStyle: 'italic',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          title={`${inherited.valueCode} – ${inherited.valueName} · inherited from ${inherited.sourceNodeName} (${inherited.sourceNodeCode})`}
        >
          {inherited.valueName} · inherited from {inherited.sourceNodeName}
        </Typography>
      )}
    </Box>
  );
};

export const InheritedLookupControl = withJsonFormsControlProps(InheritedLookupComponent);

export const inheritedLookupEntry = {
  tester: inheritedLookupTester,
  renderer: InheritedLookupControl,
};
